import { QK } from '@/js/consts/query-keys';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import {
    getRoles,
    getRole,
    createRole,
    updateRole,
    deleteRole,
    getRolePermissions,
    updateRolePermissions,
} from './roleApi';
import type { RequestCreateRole, RequestUpdateRole } from './types';

// Roles

export const useQueryGetRoles = () => {
    return useQuery({
        queryKey: [QK.ROLES.ALL],
        queryFn: async () => getRoles(),
    });
};

export const useQueryGetRole = (roleId: MaybeRefOrGetter<string>) => {
    return useQuery({
        queryKey: computed(() => [QK.ROLES.GET, toValue(roleId)]),
        queryFn: async () => getRole(toValue(roleId)),
        enabled: () => !!toValue(roleId),
    });
};

export const useMutationCreateRole = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: RequestCreateRole) => createRole(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.ROLES.ALL] });
        },
    });
};

export const useMutationUpdateRole = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ roleId, ...data }: { roleId: string } & RequestUpdateRole) =>
            updateRole(roleId, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [QK.ROLES.ALL] });
            queryClient.invalidateQueries({ queryKey: [QK.ROLES.GET, variables.roleId] });
        },
    });
};

export const useMutationUpdateRolePermissions = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            roleId,
            rolePermissions,
        }: {
            roleId: string;
            rolePermissions: Record<string, number>;
        }) => updateRolePermissions(roleId, rolePermissions),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [QK.ROLES.PERMISSIONS, variables.roleId] });
        },
    });
};

export const useQueryGetRolePermissions = (roleId: MaybeRefOrGetter<string>) => {
    return useQuery({
        queryKey: computed(() => [QK.ROLES.PERMISSIONS, toValue(roleId)]),
        queryFn: async () => getRolePermissions(toValue(roleId)),
        enabled: () => !!toValue(roleId),
    });
};

export const useMutationDeleteRole = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (roleId: string) => deleteRole(roleId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.ROLES.ALL] });
        },
    });
};
